import LoadingBlock from '@/components/feedback/LoadingBlock';
import { useApiCall } from '@/hooks/useApiCall';
import { FunctionComponent, useEffect } from 'react';
import { toast } from 'react-toastify';
import LocationFormLogic from './LocationFormLogic';
import { LocationFormModel } from './LocationFormModel';
import { useNavigate, useParams } from 'react-router-dom';
import { isNumber } from 'lodash-es';
import { LocationModel } from '../../types/LocationModel';
import { locationService } from '../../api/locations/location.service';
import { ApiResponseCode } from '@/lib/api/api-response-code';
import { useUser } from '@/modules/users/contexts/UserContext';
import { Permission } from '@/modules/users/submodules/roles/api/permissions.contracts';
import { useTranslation } from '@/lib';
import { LocationType } from '../../api/locations/location.contracts';

const LocationFormApi: FunctionComponent = () => {
  const { locationId } = useParams();
  const locationIdNumber = locationId ? Number.parseInt(locationId) : 0;
  const isCreate = locationId === 'create';
  const { t } = useTranslation();

  const navigate = useNavigate();
  const hasLocationEditPermission = useUser().hasPermission(Permission.LOCATIONS_EDIT);

  const handleSubmit = async (data: LocationFormModel) => {
    if (!hasLocationEditPermission) {
      toast.error(t('location_no_permission'));
      return false;
    }

    // Process data
    if (isCreate) {
      const response = await locationService.create({
        name: data.name,
        code: data.code,
        description: data.description,
        externalId: data.externalId,
        geofence: data.geofence,
        country: data.country,
        city: data.city,
        postalCode: data.postalCode,
        streetName: data.streetName,
        streetNumber: data.streetNumber,
        parentLocationId: data.parentLocationId,
        includeInReturnRate: data.includeInReturnRate,
        type: data.type,
      });

      if (!response.isSuccess) {
        if (response.responseCode === ApiResponseCode.DUPLICATE_ENTITY_CODE) {
          toast(t('location_code_already_exists'));
        } else {
          toast(t('create_location_error'), {
            type: 'error',
          });
        }
      } else {
        toast.success(t('create_location_success'));
        navigate('/app/locations/overview');
      }
    } else if (isNumber(locationIdNumber)) {
      const response = await locationService.update(locationIdNumber, {
        name: data.name,
        code: data.code,
        description: data.description,
        externalId: data.externalId,
        geofence: data.geofence,
        country: data.country,
        city: data.city,
        postalCode: data.postalCode,
        streetName: data.streetName,
        streetNumber: data.streetNumber,
        parentLocationId: data.parentLocationId,
        includeInReturnRate: data.includeInReturnRate,
        type: data.type,
      });

      if (!response.isSuccess) {
        if (response.responseCode === ApiResponseCode.DUPLICATE_ENTITY_CODE) {
          toast(t('location_code_already_exists'));
        } else {
          toast(t('update_location_error'), {
            type: 'error',
          });
        }
      } else {
        toast.success(t('update_location_success'));
        navigate('/app/locations/overview/');
      }
    }
    return true;
  };

  if (isCreate) {
    const newLocation: LocationFormModel = {
      name: '',
      code: '',
      description: '',
      externalId: undefined,
      geofence: undefined,
      country: undefined,
      city: undefined,
      postalCode: undefined,
      streetNumber: undefined,
      streetName: undefined,
      parentLocationId: undefined,
      isSystem: false,
      includeInReturnRate: true,
      type: LocationType.DEPO,
    };
    return <LocationFormLogic initialValues={newLocation} onSubmit={handleSubmit} isCreate />;
  } else {
    const { data, isLoading, isError, setApiCallArg } = useApiCall<LocationModel>(() => locationService.getById(0), {
      manual: true,
    });

    useEffect(() => {
      if (!isCreate && locationIdNumber) {
        setApiCallArg(() => locationService.getById(locationIdNumber));
      }
    }, [locationIdNumber]);

    // return early if initial form data isn't loaded
    if (isLoading) return <LoadingBlock />;
    if (isError || !data) return <div>{t('no_data')}</div>;

    return (
      <LocationFormLogic
        initialValues={{
          name: data.dto.name,
          code: data.dto.code,
          description: data.dto.description,
          externalId: data.dto.externalId,
          city: data.dto.city,
          country: data.dto.country,
          geofence: data.dto.geofence,
          parentLocationId: data.parentLocationId,
          postalCode: data.dto.postalCode,
          streetName: data.dto.streetName,
          streetNumber: data.dto.streetNumber,
          isSystem: data.dto.isSystem,
          includeInReturnRate: data.dto.includeInReturnRate,
          type: data.dto.type,
        }}
        onSubmit={handleSubmit}
        isCreate={isCreate}
        locationId={locationIdNumber}
      />
    );
  }
};

export default LocationFormApi;
